import { useLocation } from "wouter"
import { Button } from "@/components/ui/button"
import { ArrowRight, CheckCircle2 } from "lucide-react"
import type { CockpitStatus, CockpitWirkung } from "@shared/dashboard"
import { berlinWeekdayTime, formatDelta, weekLabel } from "@/lib/cockpitFormat"

// Status header: answers "läuft es, was kommt als Nächstes" in one glance. Replaces the
// greeting. The approve button renders ONLY while posts wait for the customer.

interface StatusHeaderProps {
  status: CockpitStatus
  wirkung: CockpitWirkung | null
  pendingApprovals: { count: number; href: string }
}

function Summary({ status, wirkung }: { status: CockpitStatus; wirkung: CockpitWirkung | null }) {
  const total = status.published + status.scheduled
  const reachDelta = wirkung ? formatDelta(wirkung.reach.deltaPct) : null
  return (
    <p className="mt-2 text-[15px] text-muted-foreground" data-testid="text-status-summary">
      {total > 0 ? (
        <>
          <strong className="font-semibold text-foreground tabular-nums">{status.published} von {total}</strong>{" "}
          {total === 1 ? "Beitrag" : "Beiträgen"} veröffentlicht
          {status.scheduled > 0 && (
            <>, <strong className="font-semibold text-foreground tabular-nums">{status.scheduled}</strong> geplant</>
          )}
          .
        </>
      ) : status.waiting > 0 ? (
        <>Ihr Wochenplan ist fertig und wartet auf Ihre Freigabe.</>
      ) : (
        <>Noch nichts veröffentlicht oder geplant.</>
      )}
      {reachDelta && (
        <>
          {" "}Reichweite in 4 Wochen{" "}
          <strong className="font-semibold text-foreground tabular-nums">{reachDelta}</strong>.
        </>
      )}
    </p>
  )
}

export function StatusHeader({ status, wirkung, pendingApprovals }: StatusHeaderProps) {
  const [, setLocation] = useLocation()
  const next = status.nextPost

  return (
    <header className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto] md:items-end" data-testid="cockpit-status">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {weekLabel(status.weekIso)}, {status.rangeLabel}
        </p>
        <h1 className="mt-1 font-playfair text-3xl font-bold leading-tight tracking-tight text-foreground" data-testid="text-status-headline">
          {status.headline}
        </h1>
        <Summary status={status} wirkung={wirkung} />
      </div>

      <div className="flex flex-col items-stretch gap-2 md:items-end">
        {pendingApprovals.count > 0 && (
          <Button
            onClick={() => setLocation(pendingApprovals.href)}
            className="justify-center"
            data-testid="button-approve-pending"
          >
            <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
            {pendingApprovals.count === 1 ? "1 Beitrag freigeben" : `${pendingApprovals.count} Beiträge freigeben`}
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Button>
        )}
        {next && (
          <button
            type="button"
            onClick={() => next.href && setLocation(next.href)}
            disabled={!next.href}
            className="flex items-center gap-3 rounded-lg border border-border bg-card px-3.5 py-2.5 text-left text-[13px] transition-colors hover:bg-muted/60 disabled:cursor-default disabled:hover:bg-card"
            data-testid="next-post"
          >
            <span className="h-2.5 w-2.5 flex-none rounded-full bg-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block font-semibold text-foreground">
                Nächster Beitrag: {berlinWeekdayTime(next.at)}, {next.platform}
              </span>
              <span className="block truncate text-xs text-muted-foreground">„{next.title}“</span>
            </span>
          </button>
        )}
      </div>
    </header>
  )
}
